import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FaStar } from "react-icons/fa";
import api from "../utils/api";
import { productService } from "../services/apiService";
import { recommendationService } from "../services/recommendationService";

const PLACEHOLDER_IMG = "https://via.placeholder.com/200x200?text=Part";

/** Fallback reviews when API returns none — matches PDP reference layout */
const FALLBACK_REVIEWS = [
  {
    id: "fb1",
    userName: "Rahul S.",
    rating: 5,
    text: "Genuine Bosch quality. Fit was perfect on my Swift. Delivery was quick and packaging was solid.",
    date: "2025-01-12",
  },
  {
    id: "fb2",
    userName: "Amit M.",
    rating: 4,
    text: "Good value. Braking feels smoother than the old pads. Would buy again.",
    date: "2025-01-08",
  },
];

const formatReviewDate = (d) => {
  if (!d) return "";
  try {
    const dt = typeof d === "string" ? new Date(d) : d;
    return dt.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return "";
  }
};

const initialsFromName = (name) => {
  if (!name || typeof name !== "string") return "?";
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const StarRow = ({ value, size = "sm" }) => {
  const v = Math.min(5, Math.max(0, Number(value) || 0));
  const filledCount = Math.round(v);
  const sz = size === "lg" ? "text-lg" : "text-sm";
  return (
    <span className={`flex items-center gap-0.5 ${sz}`} aria-hidden>
      {[1, 2, 3, 4, 5].map((i) => (
        <FaStar key={i} className={i <= filledCount ? "text-amber-400" : "text-slate-600"} />
      ))}
    </span>
  );
};

export const ProductDetailCustomerReviews = ({ productId, summaryRating, reviewCount }) => {
  const [reviews, setReviews] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await api.get(`/reviews/product/${productId}`);
        const data = res.data?.data || res.data;
        const list = data?.reviews || data;
        if (!cancelled && Array.isArray(list) && list.length > 0) {
          setReviews(list);
        } else if (!cancelled) {
          setReviews(FALLBACK_REVIEWS);
        }
      } catch {
        if (!cancelled) setReviews(FALLBACK_REVIEWS);
      } finally {
        if (!cancelled) setLoaded(true);
      }
    };
    if (productId) load();
    return () => {
      cancelled = true;
    };
  }, [productId]);

  const avg =
    reviews.length > 0
      ? reviews.reduce((s, r) => s + (Number(r.rating) || 0), 0) / reviews.length
      : summaryRating;
  const count = reviews.length > 0 ? reviews.length : reviewCount;

  const normalized = reviews.map((r) => ({
    id: r.id || r._id,
    userName: r.userName || r.user?.name || r.name || "Customer",
    rating: Number(r.rating) || 5,
    text: r.text || r.comment || "",
    date: r.date || r.createdAt,
  }));

  if (!loaded) {
    return (
      <div className="rounded-xl border border-slate-700/90 bg-slate-900/40 p-5 sm:p-6 animate-pulse">
        <div className="h-6 bg-slate-800 rounded w-1/3 mb-6" />
        <div className="space-y-4">
          <div className="h-24 bg-slate-800/80 rounded-lg" />
          <div className="h-24 bg-slate-800/80 rounded-lg" />
        </div>
      </div>
    );
  }

  return (
    <section className="rounded-xl border border-slate-700/90 bg-slate-900/35 p-5 sm:p-6">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <h2 className="text-[11px] sm:text-xs font-black uppercase tracking-[0.18em] text-slate-400">
          Customer reviews
        </h2>
        <div className="flex flex-wrap items-center gap-2 sm:gap-3 ml-auto">
          <StarRow value={avg} size="lg" />
          <span className="text-lg font-black text-white tabular-nums">{avg.toFixed(1)}</span>
          <span className="text-sm text-slate-500">{count} reviews</span>
        </div>
      </div>

      <div className="divide-y divide-slate-800">
        {normalized.map((review, idx) => (
          <article key={review.id || `rv-${idx}`} className="py-5 first:pt-0 last:pb-0">
            <div className="flex gap-3 sm:gap-4">
              <div
                className="shrink-0 w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center text-xs font-black text-white"
                aria-hidden
              >
                {initialsFromName(review.userName)}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                  <p className="font-bold text-white text-sm">{review.userName}</p>
                  <time className="text-xs text-slate-500 shrink-0">{formatReviewDate(review.date)}</time>
                </div>
                <div className="mt-1 mb-2">
                  <StarRow value={review.rating} />
                </div>
                <p className="text-sm text-slate-400 leading-relaxed">
                  {review.text || "—"}
                </p>
              </div>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
};

export const ProductDetailRelatedParts = ({ currentProduct }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!currentProduct) {
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        let allProducts = [];
        const result = await productService.getUserProducts({
          category: currentProduct.category,
          limit: 50,
        });
        allProducts = result.products || result.data?.products || [];
        const mapped = allProducts.map((product) => ({
          id: product._id || product.id,
          name: product.name,
          brand: product.brand,
          price: product.price || 0,
          image: product.images?.[0] || product.imageUrl || PLACEHOLDER_IMG,
          category: product.category,
          partNumber: product.partNumber,
        }));
        let rec = recommendationService.getSimilarProducts(mapped, currentProduct);
        if (rec.length === 0) {
          rec = mapped.filter((p) => p.id !== currentProduct.id);
        }
        rec = rec.slice(0, 5);
        if (!cancelled) setItems(rec);
      } catch {
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [currentProduct]);

  return (
    <section className="rounded-xl border border-slate-700/90 bg-slate-900/35 p-5 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <h2 className="text-[11px] sm:text-xs font-black uppercase tracking-[0.2em] text-slate-400">
          Related parts
        </h2>
        <Link
          to="/catalog"
          className="text-sm font-bold text-amber-500 hover:text-amber-400 inline-flex items-center gap-1"
        >
          View all <span aria-hidden>→</span>
        </Link>
      </div>

      {loading ? (
        <div className="flex gap-3 overflow-hidden">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="shrink-0 w-[140px] sm:w-[160px] h-48 rounded-lg bg-slate-800/80 animate-pulse" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <p className="text-sm text-slate-500 text-center py-8">No related parts found.</p>
      ) : (
        <div className="flex gap-3 sm:gap-4 overflow-x-auto pb-2 scrollbar-thin -mx-1 px-1">
          {items.map((p) => (
            <Link
              key={p.id}
              to={`/catalog/part-p-${p.id}`}
              state={{ product: { ...p, image: p.image }, category: { name: currentProduct?.category } }}
              className="group shrink-0 w-[140px] sm:w-[160px] rounded-xl border border-slate-700 bg-slate-800/60 hover:bg-slate-800 hover:border-slate-600 overflow-hidden transition-colors"
            >
              <div className="aspect-square bg-slate-950/80 flex items-center justify-center p-3 border-b border-slate-700/80">
                <img
                  loading="lazy"
                  src={p.image}
                  alt=""
                  className="max-w-full max-h-full object-contain opacity-90 group-hover:opacity-100"
                  onError={(e) => {
                    e.target.src = PLACEHOLDER_IMG;
                  }}
                />
              </div>
              <div className="p-3">
                <p className="text-[11px] sm:text-xs font-semibold text-white line-clamp-2 min-h-[2.5rem] leading-snug">
                  {p.brand ? `${p.brand} ` : ""}
                  {p.name}
                </p>
                <p className="mt-2 text-sm font-black text-white tabular-nums">
                  ₹{Number(p.price).toLocaleString("en-IN")}
                </p>
              </div>
            </Link>
          ))}
        </div>
      )}
    </section>
  );
};

const ProductDetailLowerSections = ({ product, summaryRating, reviewCount }) => {
  if (!product?.id) return null;
  return (
    <div className="mt-10 sm:mt-12 space-y-8">
      <ProductDetailCustomerReviews
        productId={product.id}
        summaryRating={summaryRating}
        reviewCount={reviewCount}
      />
      <ProductDetailRelatedParts currentProduct={product} />
    </div>
  );
};

export default ProductDetailLowerSections;
